import { createSlice } from "@reduxjs/toolkit";
import { SUCCESS_STATUS } from "../constants";
import { request } from "../service/api";
import { setError } from "./appSlice";
import { LOADING_KEYS } from "../constants/loadingKeys";

export const homeSlice = createSlice({
  name: "home",
  initialState: {
    layoutDetails: null,
    riskassessments: [],
    selectedRiskAssessment: null,
    skinHealthProducts: [],
    skinHealthMetadata: null,
    skinHealthSearchTerm: "",
    skinHealthRequestId: 0,
    divisionDescription: [],
    formularyFacilities: [],
    selectedFacility: null,
    productDivision: [],
    patientTypes: [],
  },
  reducers: {
    setLayoutDetails: (state, action) => {
      state.layoutDetails = action.payload;
    },
    setRiskAssessments: (state, action) => {
      state.riskassessments = action.payload;
    },
    setSelectedRiskAssessment: (state, action) => {
      state.selectedRiskAssessment = action.payload;
    },
    setBeginSkinHealthRequest: (state, action) => {
      const { search, isNewSearch } = action.payload;
      state.skinHealthRequestId += 1;
      state.skinHealthSearchTerm = search;
      if (isNewSearch) {
        state.skinHealthProducts = [];
        state.skinHealthMetadata = null;
      }
    },
    setSkinHealthProducts: (state, action) => {
      const { items, metadata } = action.payload;
      const existingIds = new Set(state.skinHealthProducts.map((p) => p.productFamilyId));
      const deduped = items.filter((item) => !existingIds.has(item.productFamilyId));
      state.skinHealthProducts = [...state.skinHealthProducts, ...deduped];
      state.skinHealthMetadata = metadata;
    },
    resetSkinHealthProducts: (state) => {
      state.skinHealthProducts = [];
      state.skinHealthMetadata = null;
      state.skinHealthSearchTerm = "";
      state.skinHealthRequestId += 1;
    },
    setDivisionDescription: (state, action) => {
      state.divisionDescription = action.payload;
    },
    //Formulary
    setFormularyFacilities: (state, action) => {
      state.formularyFacilities = action.payload;
    },
    setSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload;
    },
    setProductDivision: (state, action) => {
      state.productDivision = action.payload;
    },
    setPatientTypes: (state, action) => {
      state.patientTypes = action.payload;
    },
  },
});

export const {
  setLayoutDetails,
  setRiskAssessments,
  setSelectedRiskAssessment,
  setBeginSkinHealthRequest,
  setSkinHealthProducts,
  resetSkinHealthProducts,
  setDivisionDescription,
  setFormularyFacilities,
  setSelectedFacility,
  setProductDivision,
  setPatientTypes,
} = homeSlice.actions;

export const getLayoutDetailsAsync = () => async (dispatch) => {
  try {
    dispatch(setLayoutDetails({}));
    const resp = await request("/layout?presetlocation=skinhealthhomepage&viewport=mobile", {
      method: "GET",
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      dispatch(setLayoutDetails(resp.data));
    }
  } catch (error) {
    const obj = { ...error };
    dispatch(setError(obj.errorMessage));
  }
};

export const getRiskAssessmentAsync = () => async (dispatch) => {
  try {
    dispatch(setRiskAssessments([]));
    dispatch(setSelectedRiskAssessment(null));
    const resp = await request("/v1/user/riskassessments", {
      method: "GET",
      loadingKey: LOADING_KEYS.GET_RISK_ASSESSMENT,
      skipToast: true,
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      const riskAssessmentList = resp?.data?.productSelector ?? [];
      dispatch(setRiskAssessments(riskAssessmentList));
      if (riskAssessmentList?.length > 0) {
        dispatch(getRiskAssessmentByIdAsync(riskAssessmentList[0]?.id, riskAssessmentList[0]?.name));
      }
    }
  } catch (error) {
    dispatch(setError(error.errorMessage));
  }
};

export const createRiskAssessmentAsync = (payload, id) => async (dispatch) => {
  try {
    const data = await request(`/user/riskassessment/${id}`, {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.CREATE_RISK_ASSESSMENT,
    });
    if (data && data.status === SUCCESS_STATUS) {
      return data.data;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const saveRiskAssessmentAsync = (payload, id) => async (dispatch) => {
  try {
    const data = await request(`/user/riskassessment/${id}`, {
      method: "PATCH",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SAVE_RISK_ASSESSMENT,
    });
    if (data && data.status === SUCCESS_STATUS) {
      dispatch(getRiskAssessmentAsync());
      return SUCCESS_STATUS;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const getRiskAssessmentByIdAsync = (id, name) => async (dispatch) => {
  try {
    dispatch(setSelectedRiskAssessment(null));
    const resp = await request(`/user/riskassessment/${id}`, {
      method: "GET",
      loadingKey: LOADING_KEYS.GET_RISK_ASSESSMENT_BY_ID,
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      dispatch(setSelectedRiskAssessment({ ...resp?.data?.braden, assessmentName: name }));
      return resp?.data?.braden;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const emailRecommendedProductAsync = (payload, id) => async (dispatch) => {
  try {
    const data = await request(`/user/riskassessment/${id}/emailrecommendedproduct`, {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.EMAIL_RECOMMENDED_PRODUCT,
    });
    if (data && data.status === SUCCESS_STATUS) {
      return data.message;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

// search product
export const getSkinHealthProducts = (payload) => async (dispatch, getState) => {
  const { search, division, offset = 0, limit = 50 } = payload;
  const isNewSearch = offset === 0;

  dispatch(setBeginSkinHealthRequest({ search, isNewSearch }));
  const requestId = getState().home.skinHealthRequestId;

  try {
    const response = await request(
      `/Products/master/nativeapp?division=${division}&search=${encodeURIComponent(search)}&offset=${offset}&limit=${limit}`,
      {
        method: "GET",
        loadingKey: LOADING_KEYS.GET_SKIN_HEALTH_PRODUCTS,
      }
    );

    if (getState().home.skinHealthRequestId !== requestId) return;

    if (response && response.status === SUCCESS_STATUS) {
      const items = response?.data?.productMaterials || [];
      const metadata = response?.data?.metadata || null;
      dispatch(setSkinHealthProducts({ items, metadata }));
    }
  } catch (error) {
    if (getState().home.skinHealthRequestId !== requestId) return;
    dispatch(setError(error.errorMessage));
  }
};

export const getProductMasterByIdAsync = (id) => async () => {
  try {
    const resp = await request(`/products/master/ProductFamily/${id}/nativeapp?familycode=`, {
      method: "GET",
      loadingKey: LOADING_KEYS.GET_PRODUCT_MASTER_BY_ID,
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      return resp?.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const getProductMasterMaterialByIdAsync = (id, facilityNumber) => async () => {
  const params = new URLSearchParams();
  if (facilityNumber) {
    params.append("FacilityNumber", facilityNumber);
    // params.append("materialnumber", id);
  }
  try {
    const resp = await request(`/products/master/material/${id}/nativeapp?${params.toString()}`, {
      method: "GET",
      loadingKey: LOADING_KEYS.GET_PRODUCT_MASTER_MATERIAL_BY_ID,
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      return resp?.data;
    }
    return null;
  } catch (error) {
    return null;
  }
};

export const createContactSalesRepresentative = (payload) => async (dispatch) => {
  try {
    const data = await request(`/sendemail/master/contactsalesrep`, {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.EMAIL_CONTACT_SALE_REP,
    });
    if (data && data.status === SUCCESS_STATUS) {
      return data.message;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const getDivisionDescription = () => async (dispatch) => {
  try {
    dispatch(setDivisionDescription([]));
    const resp = await request(`/listoptions/divisiondescription`, {
      method: "GET",
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      dispatch(setDivisionDescription(resp?.data?.options));
    }
  } catch (error) {
    dispatch(setError(error.errorMessage));
  }
};

export const sendProductNotFoundEmailAsync = (payload) => async (dispatch) => {
  try {
    const data = await request(`/sendemail/master/productnotfound`, {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.PRODUCT_NOT_FOUND_EMAIL,
    });
    if (data && data.status === SUCCESS_STATUS) {
      return data.message;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const getFormularyFacilities = (customerNumber) => async (dispatch) => {
  try {
    dispatch(setFormularyFacilities([]));
    dispatch(setSelectedFacility(null));
    const resp = await request(`/listoptions/formularyfacility?CustomerNumber=${customerNumber}`, {
      method: "GET",
      loadingKey: LOADING_KEYS.GET_FORMULARY_FACILITIES,
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      const facilities = resp?.data?.facilities ?? [];
      dispatch(setFormularyFacilities(facilities));
      if (facilities?.length > 0) {
        dispatch(setSelectedFacility(facilities[0]));
      }
    }
  } catch (error) {
    dispatch(setError(error.errorMessage));
  }
};

export const getProductDivision = (id) => async (dispatch) => {
  try {
    dispatch(setProductDivision([]));
    const resp = await request(`/formulary/${id}/productdivision`, {
      method: "GET",
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      dispatch(setProductDivision(resp?.data));
    }
  } catch (error) {
    dispatch(setError(error.errorMessage));
  }
};

export const searchByProductDivision = (id, payload) => async (dispatch) => {
  try {
    const response = await request(`/productCategory/formulary/${id}`, {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.SEARCH_BY_PRODUCT_DIVISION,
    });
    if (response && response.status === SUCCESS_STATUS) {
      return response?.data;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const getPatientTypes = () => async (dispatch) => {
  try {
    dispatch(setPatientTypes([]));
    const resp = await request("/listoptions/patienttype", {
      method: "GET",
    });
    if (resp && resp.status === SUCCESS_STATUS) {
      dispatch(setPatientTypes(resp?.data?.options));
    }
  } catch (error) {
    dispatch(setError(error.errorMessage));
  }
};

export const getRecommendedProtein =
  ({ weight, patientType }) =>
  async (dispatch) => {
    try {
      const resp = await request(`/proteincalculator/recommended?weight=${weight}&patientType=${patientType}`, {
        method: "GET",
        loadingKey: LOADING_KEYS.GET_RECOMMENDED_PROTEIN,
      });
      if (resp && resp.status === SUCCESS_STATUS) {
        return resp?.data; // 56 grams/day
      }
    } catch (error) {
      dispatch(setError(error.errorMessage));
      return null;
    }
  };

export const getSupplementProtein =
  ({ weight, patientType, dailyProtein }) =>
  async (dispatch) => {
    try {
      const resp = await request(`/proteincalculator/supplement?weight=${weight}&patientType=${patientType}&dailyProtein=${dailyProtein}`, {
        method: "GET",
        loadingKey: LOADING_KEYS.GET_SUPPLEMENT_PROTEIN,
        skipToast: true,
      });
      if (resp && resp.status === SUCCESS_STATUS) {
        return { type: "next", response: resp?.data };
      }
      return null;
    } catch (error) {
      const obj = { ...error }
      return { type: "current", response: obj.errorMessage};
    }
  };

// submitorpadticket
export const createTableOrStrtcherPads = (payload) => async (dispatch) => {
  try {
    const response = await request("/submitorpadticket", {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.CREATE_TABLE_PADS,
    });
    if (response && response.status === SUCCESS_STATUS) {
      return SUCCESS_STATUS;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

// product selector

export const getProductSelectorQuestion = (payload) => async (dispatch) => {
  try {
    const data = await request(
      "/question",
      {
        method: "POST",
        body: JSON.stringify(payload),
        loadingKey: LOADING_KEYS.PRODUCT_SELECTOR_QUESTION,
      },
      "CHATBOT"
    );

    if (data && data.status === SUCCESS_STATUS) {
      return { success: true, data: data.data, results: data.results ?? null };
    }
    return { success: false };
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return { success: false, error };
  }
};

export const getRecommendedproductcount = (payload) => async (dispatch) => {
  try {
    const data = await request("/chatbot/productselector/getrecommendedproductcount", {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.GET_RECOMMENDED_PRODUCT_COUNT,
      skipToast: true,
    });

    if (data && data.status === SUCCESS_STATUS) {
      return data?.data;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const getRecommendedProductDetail = (payload) => async (dispatch) => {
  try {
    const data = await request("/chatbot/productselector/getrecommendedproductdetail", {
      method: "POST",
      body: JSON.stringify(payload),
      loadingKey: LOADING_KEYS.GET_RECOMMENDED_PRODUCT_DETAIL,
    });

    if (data && data.status === SUCCESS_STATUS) {
      return data?.data;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export const sendNonPaidRequestDemoEmail = () => async (dispatch) => {
  try {
    const data = await request(`/patients/assessment/nonpaid/sendemail/requestdemo`, {
      method: "PATCH",
      body: JSON.stringify({
        uuid: crypto.randomUUID()
      }),
      loadingKey: LOADING_KEYS.NON_PAID_REQUEST_DEMO,
    });
    if (data && data.status === SUCCESS_STATUS) {
      return data.message;
    }
    return null;
  } catch (error) {
    dispatch(setError(error.errorMessage));
    return null;
  }
};

export default homeSlice.reducer;
